/**
 * Builds the Excel/Word/PDF export for Board Minutes & Reports findings.
 *
 * The on-screen table is the source of truth — not `summarizationTasks.output`,
 * which is a counts-and-narrative dump. The export is the unfiltered register:
 * one row per active finding, matching the table columns.
 *
 * Dates are written as real Date cells so the practitioner can sort in Excel.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import type { BoardReportFindingUI } from "./types";

const DOC_TYPE_LABELS: Record<string, string> = {
  board_minutes: "Minutes",
  board_report: "Report",
  circular_resolution: "Resolution",
  pre_appointment_minutes: "Pre-appt",
};

const FLAG_LABELS: Record<string, string> = {
  financial_distress: "Financial distress",
  turnaround: "Turnaround",
  safe_harbour: "Safe harbour",
  material_transaction: "Material transaction",
};

const CONFIDENCE_LABELS: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

function asDate(raw: string): Date | "" {
  if (!raw) return "";
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? "" : parsed;
}

function flagsLabel(flags: string[]): string {
  return flags.map((flag) => FLAG_LABELS[flag] ?? flag).join("; ");
}

function findingsSheet(entries: BoardReportFindingUI[]): ExportSheet {
  const sorted = [...entries]
    .filter((e) => e.status === "active")
    .sort((a, b) => b.date.localeCompare(a.date));

  return {
    name: "Board Minutes & Reports",
    headers: ["Date", "Type", "Summary", "Flags", "Confidence"],
    rows: sorted.map((finding) => [
      asDate(finding.date),
      DOC_TYPE_LABELS[finding.documentType] ?? finding.documentType,
      finding.summary,
      flagsLabel(finding.flags),
      CONFIDENCE_LABELS[finding.confidence] ?? finding.confidence,
    ]),
  };
}

export function buildBoardReportsExport(
  entries: BoardReportFindingUI[],
): ExportContent {
  return {
    title: "Board Minutes & Reports",
    sheets: [findingsSheet(entries)],
  };
}
